/**
 * File Parameter Resolver
 *
 * Pure utility that identifies [file] parameters in a parameter set,
 * computes deterministic mount paths for the worker virtual filesystem,
 * and decodes data-URL payloads into byte arrays suitable for FS.writeFile.
 *
 * Desktop OpenSCAD resolves import() paths relative to the source file
 * directory, then searches OPENSCADPATH. We replicate this by mounting
 * file parameter contents next to the main SCAD file in the worker FS.
 *
 * @license GPL-3.0-or-later
 */

/**
 * Raster image extensions recognized for PNG-to-SVG conversion.
 * Used by the UI layer to decide whether to auto-convert before mounting.
 */
export const RASTER_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'bmp', 'gif'];

/**
 * Check if a filename has a raster image extension.
 * Worker-safe (pure string operation, no DOM dependency).
 *
 * @param {string} fileName
 * @returns {boolean}
 */
export function isRasterImageFile(fileName) {
  if (!fileName || typeof fileName !== 'string') return false;
  const ext = fileName.split('.').pop().toLowerCase();
  return RASTER_IMAGE_EXTENSIONS.includes(ext);
}

/**
 * Sanitize URL parameters against extracted schema.
 * @param {Object|null} extracted - Parsed schema from extractParameters()
 * @param {Object} urlParams - Raw URL parameter values
 * @returns {{ sanitized: Object, adjustments: Object }}
 */
export function sanitizeUrlParams(extracted, urlParams) {
  const sanitized = {};
  const adjustments = {};

  for (const [key, value] of Object.entries(urlParams || {})) {
    const schema = extracted?.parameters?.[key];
    if (!schema) {
      adjustments[key] = { reason: 'unknown-param', value };
      continue;
    }

    if (Array.isArray(schema.enum)) {
      if (!schema.enum.includes(value)) {
        adjustments[key] = { reason: 'enum', value, allowed: schema.enum };
        continue;
      }
      sanitized[key] = value;
      continue;
    }

    if (typeof value === 'number') {
      let nextValue = value;
      if (schema.minimum !== undefined && nextValue < schema.minimum) {
        adjustments[key] = {
          reason: 'min',
          value,
          minimum: schema.minimum,
          maximum: schema.maximum,
        };
        nextValue = schema.minimum;
      }
      if (schema.maximum !== undefined && nextValue > schema.maximum) {
        adjustments[key] = {
          reason: 'max',
          value,
          minimum: schema.minimum,
          maximum: schema.maximum,
        };
        nextValue = schema.maximum;
      }
      if (schema.type === 'integer') {
        nextValue = Math.round(nextValue);
      }
      sanitized[key] = nextValue;
      continue;
    }

    sanitized[key] = value;
  }

  return { sanitized, adjustments };
}

/**
 * Check whether a parameter value is a file object from the UI.
 *
 * File params arrive as { name: string, data: string (data-URL), ... }
 * from the createFileControl handler in ui-generator.js.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isFileParamValue(value) {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.name === 'string' &&
    value.name.length > 0 &&
    value.data != null
  );
}

/**
 * Decode a data-URL string into a Uint8Array.
 *
 * Handles both:
 *   - data:[<mediatype>];base64,<data>   (from FileReader.readAsDataURL)
 *   - raw base64 strings (fallback)
 *
 * @param {string} dataUrl
 * @returns {Uint8Array}
 */
export function decodeDataUrl(dataUrl) {
  if (typeof dataUrl !== 'string') {
    if (dataUrl instanceof ArrayBuffer) return new Uint8Array(dataUrl);
    if (dataUrl instanceof Uint8Array) return dataUrl;
    if (ArrayBuffer.isView(dataUrl))
      return new Uint8Array(
        dataUrl.buffer,
        dataUrl.byteOffset,
        dataUrl.byteLength
      );
    throw new Error('Unsupported file parameter data type');
  }

  let base64;
  if (dataUrl.startsWith('data:')) {
    const commaIndex = dataUrl.indexOf(',');
    if (commaIndex === -1) throw new Error('Malformed data URL: missing comma');
    base64 = dataUrl.substring(commaIndex + 1);
  } else {
    base64 = dataUrl;
  }

  const binaryStr = atob(base64);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return bytes;
}

/**
 * Whether a project path climbs out of its folder: a ".." segment, after
 * either kind of slash. Two dots inside a name ("puzzle_V2..scad") are only
 * part of the name.
 * @param {string} path
 * @returns {boolean}
 */
export function hasParentSegment(path) {
  return String(path)
    .split(/[\\/]/)
    .some((segment) => segment === '..');
}

/**
 * Sanitize a filename for safe use in the virtual filesystem.
 * Rejects path traversal and strips directory prefixes.
 *
 * Intentionally separate from download.js sanitizeFilename (download-name
 * cosmetics) and storage-manager.js sanitizeFileName (underscore
 * substitution for project paths): this one is a security guard for the
 * worker FS and must keep its traversal/basename semantics.
 *
 * @param {string} rawName
 * @returns {string}
 */
export function sanitizeFileName(rawName) {
  if (!rawName || typeof rawName !== 'string') return 'uploaded_file';

  let name = rawName.replace(/\\/g, '/');

  // Reject any path containing traversal before basename extraction
  if (name.includes('..')) {
    return 'uploaded_file';
  }

  // Strip directory path — keep only the basename
  const slashIdx = name.lastIndexOf('/');
  if (slashIdx !== -1) name = name.substring(slashIdx + 1);

  if (name === '.' || name === '..') {
    return 'uploaded_file';
  }

  // Strip leading dots (hidden files) for safety
  name = name.replace(/^\.+/, '');

  return name || 'uploaded_file';
}

/**
 * Resolve file parameters into mount operations and cleaned parameter values.
 *
 * @param {Object} parameters - Parameter key-value pairs (may contain file objects)
 * @param {string} mountBaseDir - Directory to mount files into (e.g. '/work' or '/tmp')
 * @returns {{
 *   mountOperations: Array<{paramName: string, fileName: string, mountPath: string, data: Uint8Array}>,
 *   resolvedParams: Object,
 *   fileParamNames: string[]
 * }}
 */
export function resolveFileParams(parameters, mountBaseDir) {
  if (!parameters || typeof parameters !== 'object') {
    return {
      mountOperations: [],
      resolvedParams: parameters || {},
      fileParamNames: [],
    };
  }

  const mountOperations = [];
  const resolvedParams = {};
  const fileParamNames = [];

  for (const [key, value] of Object.entries(parameters)) {
    if (isFileParamValue(value)) {
      const fileName = sanitizeFileName(value.name);
      const mountPath = `${mountBaseDir}/${fileName}`;

      try {
        const data = decodeDataUrl(value.data);
        mountOperations.push({ paramName: key, fileName, mountPath, data });
        fileParamNames.push(fileName);
      } catch (err) {
        console.warn(
          `[FileParamResolver] Failed to decode data for param "${key}":`,
          err.message
        );
        resolvedParams[key] = fileName;
        continue;
      }

      // Replace the file object with just the filename string.
      // OpenSCAD resolves import() relative to the source file dir,
      // so the bare filename matches the mounted path.
      resolvedParams[key] = fileName;
    } else {
      resolvedParams[key] = value;
    }
  }

  return { mountOperations, resolvedParams, fileParamNames };
}
